import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { TextFieldProps } from '@mui/material'
import { ProjectGridField } from '@/types/fields'
import usePickerFieldData, { getLabel } from '@/hooks/usePickerFieldData'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { AutocompleteOption } from './AutocompleteField'
import { useGetChildDataList } from '@/service-library/hooks/data-lists'
import { useGetDataListColumns } from '@/service-library/hooks/data-list-columns'
import { useGetDataListEntry } from '@/service-library/hooks/data-list-entries'
import BasePickerField from './BasePickerField'

type PickerFieldProps = {
  onChange: (newValue: string | null, refId: string | null) => void
  field: ProjectGridField
  value: AutocompleteOption | null
  setDisableArrowKeys: Dispatch<SetStateAction<boolean>>
  isInTable?: boolean
} & Omit<TextFieldProps, 'onChange' | 'value'>

export default function PickerField({
  field,
  value,
  label,
  onChange,
  ...props
}: PickerFieldProps) {
  const { document } = useDocumentContext()

  const {
    data_list_id: metadataListId,
    data_list_column_id: metadataColumnId,
  } = field.metadata // We are keeping metadata for backwards compatibility

  const {
    data_list_id: paramsListId,
    data_list_column_names: paramsColumnNames,
  } = field.params || {}

  const isListValue = !!value && value.id !== 'None'

  const [enabled, setEnabled] = useState(false)

  const [open, setOpen] = useState(false)

  // This returns either the data list, or the data list this document's organization inherits from,
  // so the listId we pass in here might be different from the one we get back. That's by design.
  const { dataList } = useGetChildDataList({
    orgId: document?.owner_org_id as string,
    id: paramsListId || metadataListId || '',
    enabled: !!document?.owner_org_id && !!(paramsListId || metadataListId),
  })

  const { dataListColumns } = useGetDataListColumns({
    filters: {
      limit: '1000',
      data_list_id: paramsListId || metadataListId || '',
    },
  })

  const { dataListEntry, isPending: listEntryIsLoading } = useGetDataListEntry({
    id: isListValue ? value.id : '',
    filters: {
      fields__include: 'data_list_entry_cells',
      data_list_entry_cells__fields__include: 'data_list_entry_cell_values',
    },
  })

  const columnsMapping = useMemo(() => {
    if (!dataListColumns.length) return {}

    return dataListColumns.reduce<Record<string, string>>((acc, column) => {
      acc[column.id] = column.name
      return acc
    }, {})
  }, [dataListColumns])

  const dataListColumnNames = useMemo(() => {
    if (paramsColumnNames) return paramsColumnNames

    if (metadataColumnId && columnsMapping[metadataColumnId])
      return [columnsMapping[metadataColumnId]]

    return []
  }, [paramsColumnNames, metadataColumnId, columnsMapping])

  const updatedValue = useMemo(() => {
    if (!isListValue || listEntryIsLoading) return value

    return {
      id: value.id,
      label:
        getLabel(
          dataListEntry?.data_list_entry_cells,
          dataListColumnNames,
          columnsMapping,
        ) ||
        value?.label ||
        '',
    }
  }, [
    columnsMapping,
    dataListColumnNames,
    dataListEntry?.data_list_entry_cells,
    isListValue,
    listEntryIsLoading,
    value,
  ])

  const updatedValueRef = useRef(updatedValue)

  const [inputValue, setInputValue] = useState(updatedValue?.label || '')
  const [searchValue, setSearchValue] = useState(updatedValue?.label || '')

  const { options, isPending, hasNextPage, fetchNextPage } = usePickerFieldData(
    {
      columnsMapping,
      dataListId: dataList?.id,
      dataListColumnNames,
      filterString: searchValue,
      value: updatedValue,
      useFilter: !!searchValue && searchValue !== updatedValue?.label,
      enabled,
      keepPreviousData: true,
    },
  )

  const dataListEntryIdRef = useRef(dataListEntry?.id)
  useEffect(() => {
    // We want to reset input and search value if a value exists when we have the dataListEntry
    if (
      updatedValue &&
      dataListEntry &&
      dataListEntryIdRef.current !== dataListEntry?.id
    ) {
      dataListEntryIdRef.current = dataListEntry.id
      updatedValueRef.current = updatedValue

      if (!inputValue) {
        setInputValue(updatedValue?.label || '')
        setSearchValue(updatedValue?.label || '')
      }
    }
  }, [dataListEntry, inputValue, updatedValue])

  return (
    <BasePickerField<AutocompleteOption>
      value={updatedValueRef.current}
      label={label}
      enabled={enabled}
      setEnabled={setEnabled}
      inputValue={inputValue}
      setInputValue={setInputValue}
      onChange={(newValue) => {
        onChange(newValue?.label || null, newValue?.id || null)
      }}
      options={options}
      open={open}
      setOpen={setOpen}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isLoading={isPending || (isListValue && listEntryIsLoading)}
      isOptionEqualToValue={(option, value) => option?.id === value.id}
      getOptionKey={(option) => option.id}
      {...props}
    />
  )
}
